/**
 * The address a visitor typed on the public "start your listing" page, kept in
 * the browser across signup so the Post Listing wizard can open with it filled
 * in. Stored in localStorage because signup can bounce through a new tab
 * (e.g. a password manager) and a session-only store would drop it.
 */

const STORAGE_KEY = 'cutthe6.pendingListing';

// Forget an abandoned start after a week rather than prefilling a stranger's
// address on a shared computer months later.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const FIELDS = ['address', 'city', 'state', 'zip_code'];

/**
 * Remember the listing address a visitor entered before signing up.
 *
 * @param {{address: string, city: string, state: string, zip_code: string}} listing
 */
export function savePendingListing(listing) {
  const value = { savedAt: Date.now() };
  FIELDS.forEach((field) => {
    value[field] = String(listing?.[field] ?? '').trim();
  });
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked (private mode, site data off): the wizard just opens empty.
  }
}

/**
 * Read the saved listing address, if there is a fresh one.
 *
 * @returns {{address: string, city: string, state: string, zip_code: string}|null}
 */
export function loadPendingListing() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearPendingListing();
      return null;
    }
    const listing = {};
    FIELDS.forEach((field) => {
      listing[field] = typeof parsed[field] === 'string' ? parsed[field] : '';
    });
    return listing.address ? listing : null;
  } catch {
    clearPendingListing();
    return null;
  }
}

/** Forget the saved listing address (once the wizard has used it). */
export function clearPendingListing() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
